import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart, User, Search } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserAuthContext";

const LINKS = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/kesfet", label: "Keşfet" },
  { to: "/programlar", label: "Programlar" },
  { to: "/magaza", label: "Mağaza" },
  { to: "/etkinlikler", label: "Etkinlikler" },
  { to: "/programlar/bilgece-atolye", label: "Bilgece Atölye" },
  { to: "/kutuphane", label: "Kütüphane" },
  { to: "/kurumlar", label: "Kurumlar İçin" },
  { to: "/hakkimizda", label: "Hakkımızda" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count } = useCart();
  const { user } = useUser();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 bg-brand-offwhite/95 backdrop-blur-md border-b transition-colors duration-300 ${
        scrolled ? "border-brand-gold-light/40 shadow-sm" : "border-transparent"
      }`}
    >
      <nav className="max-w-[1600px] mx-auto flex items-center justify-between gap-6 px-6 lg:px-10 h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 flex-shrink-0">
          <img src="/logo-mark.png" alt="Bilgece" className="h-10 w-auto" />
          <span className="font-serif text-2xl md:text-3xl font-semibold tracking-tight text-brand-green">
            BİLGECE<span className="text-brand-gold align-super text-sm">™</span>
          </span>
        </Link>

        <div className="hidden 2xl:flex items-center gap-6">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.split("/").filter(Boolean).join("-")}`}
              className={`whitespace-nowrap text-sm font-medium link-underline text-brand-text/80 hover:text-brand-green ${
                location.pathname === l.to ? "text-brand-green" : ""
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/arama" data-testid="nav-search" className="p-2 text-brand-green hover:text-brand-gold transition-colors" aria-label="Ara"><Search size={21} /></Link>
          <Link to="/sepet" data-testid="nav-cart" className="relative p-2 text-brand-green hover:text-brand-gold transition-colors" aria-label="Sepet">
            <ShoppingCart size={22} />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 bg-brand-gold text-brand-offwhite text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
          </Link>
          <Link to={user ? "/hesabim" : "/giris"} data-testid="nav-account" className="p-2 text-brand-green hover:text-brand-gold transition-colors" aria-label="Hesabım"><User size={22} /></Link>
          <Link
            to="/programlar/bilgece-atolye"
            data-testid="nav-cta-join"
            className="hidden sm:inline-block whitespace-nowrap bg-brand-gold text-brand-offwhite hover:bg-brand-green transition-colors duration-300 px-6 py-2.5 text-sm font-semibold tracking-wide rounded-sm"
          >
            Bilgece'ye Katıl
          </Link>
          <button
            data-testid="nav-hamburger"
            className="2xl:hidden p-2 text-brand-green"
            onClick={() => setOpen(!open)}
            aria-label="Menü"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="2xl:hidden absolute top-full inset-x-0 h-[calc(100dvh-5rem)] bg-brand-offwhite z-40 overflow-y-auto">
          <div className="flex flex-col px-8 py-10 gap-1">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-link-${l.to === "/" ? "home" : l.to.split("/").filter(Boolean).join("-")}`}
                className="font-serif text-3xl py-3 border-b border-brand-gold-light/30 text-brand-green"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/programlar/bilgece-atolye"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-join"
              className="mt-8 bg-brand-gold text-brand-offwhite text-center px-6 py-4 font-semibold rounded-sm"
            >
              Bilgece'ye Katıl
            </Link>
            <Link to={user ? "/hesabim" : "/giris"} onClick={() => setOpen(false)} data-testid="mobile-account" className="mt-3 border border-brand-gold text-brand-green text-center px-6 py-4 font-semibold rounded-sm">
              {user ? "Hesabım" : "Giriş Yap"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
